/**
 * 🔁 «هیچ‌وقت متوقف نشو» — جابه‌جایی بین سرویس‌ها و مدل‌ها.
 *
 * سه چیز را نشان می‌دهد و نه بیشتر:
 *   ▪ الان روی چه سرویس و چه مدلی هستیم؛
 *   ▪ اگر سرویس اصلی خراب شده، چه جایگزینی پیدا شده و آیا تأییدش می‌کنید؛
 *   ▪ سرویس‌های پشتیبان، به ترتیبی که امتحان می‌شوند.
 *
 * تأیید عمدی است: مدل بدون اجازهٔ شما عوض نمی‌شود. برگشت به مدل اصلی برعکس،
 * خودکار است و تأیید نمی‌خواهد.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Grid, MenuItem, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RadarRoundedIcon from '@mui/icons-material/RadarRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import { ResponsiveModal } from '../common';
import userbotService, { UserbotAccountTest, UserbotCandidateReport, UserbotFailoverStatus } from '../../services/userbotService';
import { EmptyHint, IconAction, Loading, Pill, SectionCard, Tag, UB, clock, errMsg, fa, faDate } from './kit';

/** پیش‌تنظیم‌های آمادهٔ سرویس پشتیبان — همان‌هایی که در تنظیمات هم هست */
const PRESETS: Array<{ label: string; baseUrl: string; gateModel: string; writerModel: string }> = [
  {
    label: 'Cloudflare Workers AI',
    baseUrl: 'https://api.cloudflare.com/client/v4/accounts/ACCOUNT_ID/ai/v1',
    gateModel: '@cf/meta/llama-3.1-8b-instruct-fast',
    writerModel: '@cf/meta/llama-3.3-70b-instruct-fp8-fast',
  },
  {
    label: 'Google Gemini',
    baseUrl: 'https://generativelanguage.googleapis.com/v1beta/openai',
    gateModel: 'gemini-flash-lite-latest',
    writerModel: 'gemini-flash-latest',
  },
  {
    label: 'OpenRouter',
    baseUrl: 'https://openrouter.ai/api/v1',
    gateModel: 'google/gemini-2.0-flash-exp:free',
    writerModel: 'deepseek/deepseek-chat-v3-0324:free',
  },
  {
    label: 'Groq',
    baseUrl: 'https://api.groq.com/openai/v1',
    gateModel: 'llama-3.1-8b-instant',
    writerModel: 'llama-3.3-70b-versatile',
  },
];

const empty = { id: '', label: '', baseUrl: '', apiKey: '', gateModel: '', writerModel: '', enabled: true };
const emptyKey = { id: '', providerId: '', label: '', apiKey: '', enabled: true };

const FailoverCard: React.FC<{ onChanged?: () => void }> = ({ onChanged }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [st, setSt] = useState<UserbotFailoverStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [reports, setReports] = useState<UserbotCandidateReport[] | null>(null);
  const [form, setForm] = useState<typeof empty | null>(null);
  const [keyForm, setKeyForm] = useState<typeof emptyKey | null>(null);
  const [keyTests, setKeyTests] = useState<UserbotAccountTest[]>([]);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSt(await userbotService.failover());
    } catch (e) {
      enqueueSnackbar(errMsg(e), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn: () => Promise<{ status: UserbotFailoverStatus; message: string }>) => {
    setBusy(true);
    try {
      const r = await fn();
      setSt(r.status);
      enqueueSnackbar(r.message, { variant: 'success' });
      onChanged?.();
    } catch (e) {
      enqueueSnackbar(errMsg(e), { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const scan = async () => {
    setScanning(true);
    setReports(null);
    try {
      const r = await userbotService.failoverScan();
      setReports(r.result.reports);
      enqueueSnackbar(r.message, { variant: r.result.winner ? 'success' : 'warning' });
    } catch (e) {
      enqueueSnackbar(errMsg(e), { variant: 'error' });
    } finally {
      setScanning(false);
    }
  };

  const testKeys = async () => {
    setTesting(true);
    setKeyTests([]);
    try {
      const r = await userbotService.testAiAccounts();
      setKeyTests(r);
      const ok = r.filter((x) => x.ok).length;
      enqueueSnackbar(ok ? `${ok} کلید از ${r.length} کلید سالم است` : 'هیچ کلیدی جواب نداد', {
        variant: ok ? 'success' : 'warning',
      });
      setSt(await userbotService.failover());
    } catch (e) {
      enqueueSnackbar(errMsg(e), { variant: 'error' });
    } finally {
      setTesting(false);
    }
  };

  if (loading && !st) return <SectionCard title="جابه‌جایی سرویس"><Loading /></SectionCard>;
  if (!st) return null;

  const ov = st.override;
  const onOverride = st.active.source === 'override';

  return (
    <Stack spacing={2}>
      {/* خودکار عوض شد — فقط خبر، نه سؤال */}
      {ov?.status === 'active' && ov.auto && !ov.acknowledged && (
        <div className="fac-insight info" style={{ display: 'block' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <SwapHorizRoundedIcon style={{ fontSize: 18, color: UB.info, marginTop: 2 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <b>سرویس خودکار عوض شد تا ربات خاموش نشود. کاری از شما لازم نیست.</b>
              <div className="fac-line-note">علت: {ov.reason}</div>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center', margin: '8px 0' }}>
                <Pill label={ov.providerLabel} color={UB.info} />
                <Tag label={`نویسنده: ${ov.writerModel}`} />
                <Tag label={`دروازه‌بان: ${ov.gateModel}`} />
                <Tag color="success" label={ov.probe} />
              </div>
              <div className="fac-line-note">به‌محض برگشتن سرویس اصلی، خودکار به همان برمی‌گردیم.</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                <Button size="small" variant="contained" disabled={busy} onClick={() => act(() => userbotService.failoverAck())}>دیدم</Button>
                <Button size="small" color="inherit" startIcon={<UndoRoundedIcon />} disabled={busy} onClick={() => act(() => userbotService.failoverReject())}>
                  نمی‌خواهم، به اصلی برگرد
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* پیشنهاد در انتظار تأیید */}
      {ov?.status === 'proposed' && (
        <div className="fac-insight warn" style={{ display: 'block' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <SwapHorizRoundedIcon style={{ fontSize: 18, color: UB.warn, marginTop: 2 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <b>سرویس اصلی جواب نمی‌دهد؛ جایگزین پیدا شد و منتظر تأیید شماست.</b>
              <div className="fac-line-note">علت کنار گذاشتن سرویس اصلی: {ov.reason}</div>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center', margin: '8px 0' }}>
                <Pill label={ov.providerLabel} color={UB.accent} />
                <Tag label={`نویسنده: ${ov.writerModel}`} />
                <Tag label={`دروازه‌بان: ${ov.gateModel}`} />
                <Tag color="success" label={ov.probe} />
              </div>
              {st.waiting > 0 && (
                <div className="fac-line-note">{fa(st.waiting)} پیام منتظر همین تصمیم است. تا تأیید نکنید جواب داده نمی‌شوند، ولی از بین هم نمی‌روند.</div>
              )}
              <div className="fac-line-note fac-warn">اگر تا {fa(st.approvalTimeoutMinutes)} دقیقه تأیید نشود، خودکار اعمال می‌شود تا ربات خاموش نماند.</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                <Button size="small" variant="contained" color="warning" startIcon={busy ? <CircularProgress size={14} /> : <CheckRoundedIcon />} disabled={busy} onClick={() => act(() => userbotService.failoverApprove())}>
                  تأیید و جابه‌جایی
                </Button>
                <Button size="small" color="inherit" startIcon={<CloseRoundedIcon />} disabled={busy} onClick={() => act(() => userbotService.failoverReject())}>
                  رد کن، منتظر اصلی می‌مانم
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* وضعیت فعلی */}
      <SectionCard
        icon={<SwapHorizRoundedIcon />}
        title="سرویس فعال"
        hint="به‌محض برگشتن سرویس اصلی، خودکار و بدون تأیید به مدل اول برمی‌گردیم."
        actions={
          <>
            <Button
              size="small"
              variant="outlined"
              startIcon={scanning ? <CircularProgress size={14} /> : <RadarRoundedIcon />}
              disabled={scanning}
              onClick={scan}
            >
              سنجش همهٔ سرویس‌ها
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={busy}
              onClick={() => act(async () => { const r = await userbotService.failoverCheck(); return r; })}
            >
              بررسی الان
            </Button>
            <IconAction title="به‌روزرسانی" onClick={load}><RefreshIcon /></IconAction>
          </>
        }
      >
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
          <Pill label={st.active.label} color={onOverride ? UB.warn : UB.ok} />
          <Tag label={`نویسنده: ${st.active.writerModel}`} />
          <Tag label={`دروازه‌بان: ${st.active.gateModel}`} />
          {onOverride && <Tag color="warning" label="روی سرویس جایگزین" />}
          {!onOverride && <Tag color="success" label="روی سرویس اصلی" />}
          {st.waiting > 0 && <Tag color="warning" label={`${fa(st.waiting)} پیام منتظر`} />}
        </Box>
        {st.active.baseUrl && (
          <Typography variant="caption" color="text.secondary" sx={{ direction: 'ltr', display: 'block', textAlign: 'right', mt: 0.75 }}>
            {st.active.baseUrl}
          </Typography>
        )}

        {onOverride && ov && (
          <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary">
              از {faDate(ov.approvedAt || ov.proposedAt)} · علت: {ov.reason}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              color="inherit"
              startIcon={<UndoRoundedIcon />}
              disabled={busy}
              onClick={() => act(() => userbotService.failoverReject())}
            >
              همین حالا به اصلی برگرد
            </Button>
          </Box>
        )}

        <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Tag label={st.enabled ? 'جابه‌جایی خودکار روشن' : 'جابه‌جایی خودکار خاموش'} color={st.enabled ? 'success' : 'default'} />
          <Tag
            label={
              st.mode === 'auto'
                ? 'جابه‌جا شو و خبر بده'
                : st.mode === 'ask'
                  ? `با تأیید من (مهلت ${fa(st.approvalTimeoutMinutes)} دقیقه)`
                  : 'هیچ‌وقت جابه‌جا نشو'
            }
            color={st.mode === 'auto' ? 'success' : st.mode === 'ask' ? 'primary' : 'error'}
          />
          <Tag label={`سنجش هر ${fa(st.probeMinutes)} دقیقه`} />
          <Tag
            color={st.alertChatIds.length ? 'success' : 'warning'}
            label={st.alertChatIds.length ? `${fa(st.alertChatIds.length)} چت هشدار` : 'بدون چت هشدار'}
          />
        </Box>
      </SectionCard>

      {/* نتیجهٔ سنجش */}
      {reports && (
        <SectionCard icon={<RadarRoundedIcon />} title="نتیجهٔ سنجش" hint="هر مدل با یک درخواست واقعی امتحان شد، نه حدس.">
          <Stack spacing={1}>
            {reports.map((r) => (
              <div key={r.providerId || 'primary'} className="ubc-item">
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={800}>{r.label}</Typography>
                  {r.isPrimary && <Tag color="primary" label="اصلی" />}
                  {r.working ? (
                    <Pill label={`سالم — ${r.working.writerModel}`} color={UB.ok} />
                  ) : (
                    <Pill label="جواب نداد" color={UB.bad} />
                  )}
                </Box>
                <Stack spacing={0.25}>
                  {r.tried.map((t, i) => (
                    <Typography key={i} variant="caption" color={t.ok ? 'success.main' : 'text.secondary'} display="block">
                      {t.ok ? '✓' : '✗'} {t.model} {t.ok ? `(${fa(t.ms)}ms)` : `— ${t.message}`}
                    </Typography>
                  ))}
                </Stack>
              </div>
            ))}
          </Stack>
        </SectionCard>
      )}

      {/* کلیدها */}
      <SectionCard
        icon={<KeyRoundedIcon />}
        title="کلیدهای هر سرویس"
        hint="چند حساب روی یک سرویس بگذارید. وقتی سهمیهٔ یکی تمام شود، بی‌درنگ و بدون تأیید با بعدی ادامه می‌دهیم — مدل و کیفیت جواب عوض نمی‌شود."
        actions={
          <>
            <Button
              size="small"
              variant="outlined"
              startIcon={testing ? <CircularProgress size={14} /> : <RadarRoundedIcon />}
              disabled={testing}
              onClick={testKeys}
            >
              امتحان همهٔ کلیدها
            </Button>
            <Button size="small" variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setKeyForm({ ...emptyKey })}>
              افزودن کلید
            </Button>
          </>
        }
      >
        <Stack spacing={1.5}>
          {st.accounts.map((pv) => (
            <Box key={pv.providerId || 'primary'}>
              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mb: 0.75 }}>
                <Typography variant="body2" fontWeight={800}>{pv.providerLabel}</Typography>
                {!pv.providerId && <Tag color="primary" label="اصلی" />}
                <Box sx={{ flex: 1 }} />
                <Tag
                  color={pv.keys.some((k) => !k.cooling) ? 'success' : 'error'}
                  label={`${fa(pv.keys.filter((k) => !k.cooling).length)} از ${fa(pv.keys.length)} کلید آزاد`}
                />
                <Button
                  size="small"
                  color="inherit"
                  startIcon={<AddRoundedIcon />}
                  onClick={() => setKeyForm({ ...emptyKey, providerId: pv.providerId })}
                >
                  کلید تازه
                </Button>
              </Box>

              {pv.keys.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  هیچ کلیدی برای این سرویس نیست.
                </Typography>
              ) : (
                <Stack spacing={0.75}>
                  {pv.keys.map((k) => {
                    const test = keyTests.find((t) => t.accountId === k.id);
                    return (
                      <div
                        key={k.id}
                        className={`ubc-item ${k.active ? 'ok' : ''}`}
                        style={{ opacity: k.cooling ? 0.7 : 1 }}
                      >
                        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Typography variant="body2" fontWeight={700}>{k.label}</Typography>
                          {k.active && <Pill label="در حال استفاده" color={UB.ok} />}
                          {k.builtin && <Tag label="کلید تنظیمات" />}
                          {k.cooling ? (
                            <Pill label={k.cooldownUntil ? `استراحت تا ${clock(k.cooldownUntil)}` : 'خاموش'} color={UB.warn} />
                          ) : (
                            <Tag color="success" label="آزاد" />
                          )}
                          {k.uses > 0 && <Tag label={`${fa(k.uses)} بار`} />}
                          {test && (
                            <Pill
                              label={test.ok ? `سالم با ${test.model} (${fa(test.ms)}ms)` : `${fa(test.tried?.length ?? 1)} مدل امتحان شد، هیچ‌کدام نشد`}
                              color={test.ok ? UB.ok : UB.bad}
                              title={test.tried?.map((t) => `${t.ok ? '✓' : '✗'} ${t.model}`).join(' · ') || test.message}
                            />
                          )}
                          <Box sx={{ flex: 1 }} />
                          {k.cooling && (
                            <Button
                              size="small"
                              color="inherit"
                              disabled={busy}
                              onClick={() =>
                                act(() => userbotService.saveAiAccount({ id: k.id, providerId: pv.providerId, label: k.label, enabled: true, clearCooldown: true }))
                              }
                            >
                              الان آزادش کن
                            </Button>
                          )}
                          {!k.builtin && (
                            <>
                              <IconAction
                                title="ویرایش"
                                onClick={() => setKeyForm({ id: k.id, providerId: pv.providerId, label: k.label, apiKey: '', enabled: !k.cooling })}
                              >
                                <KeyRoundedIcon fontSize="small" />
                              </IconAction>
                              <IconAction title="حذف" color="error" disabled={busy} onClick={() => act(() => userbotService.deleteAiAccount(k.id))}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconAction>
                            </>
                          )}
                        </Box>
                        {(k.lastError || test?.message) && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                            {test?.message || k.lastError}
                          </Typography>
                        )}
                      </div>
                    );
                  })}
                </Stack>
              )}
            </Box>
          ))}
        </Stack>
      </SectionCard>

      {/* 📚 دانش مدل‌ها — چه چیزی واقعاً کار می‌کند */}
      <SectionCard
        icon={<RadarRoundedIcon />}
        title="مدل‌هایی که امتحان شده‌اند"
        hint="سیستم خودش فهرست مدل‌های هر سرویس را می‌گیرد و نتیجهٔ هر امتحان را یاد می‌گیرد؛ دفعهٔ بعد اول سراغ همان‌هایی می‌رود که جواب داده‌اند."
      >
        {!st.models?.length ? (
          <EmptyHint
            icon={<RadarRoundedIcon />}
            title="هنوز مدلی امتحان نشده"
            hint="با «سنجش همین حالا» فهرست مدل‌های هر سرویس گرفته و امتحان می‌شود."
          />
        ) : (
          <Stack spacing={0.75}>
            {st.models.slice(0, 12).map((m) => (
              <div key={`${m.hostKey}:${m.model}`} className="ubc-item" style={{ opacity: m.dead ? 0.6 : 1 }}>
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography variant="body2" fontWeight={800} sx={{ fontFamily: 'monospace', direction: 'ltr' }}>
                    {m.model}
                  </Typography>
                  {m.proven && !m.dead && <Pill label="آزموده" color={UB.ok} />}
                  {m.dead && <Pill label="فعلاً کنار گذاشته" color={UB.warn} />}
                  {m.providerLabel && <Tag label={m.providerLabel} />}
                  <Box sx={{ flex: 1 }} />
                  {m.ok > 0 && <Tag label={`${fa(m.ok)} موفق`} />}
                  {m.fail > 0 && <Tag label={`${fa(m.fail)} ناموفق`} />}
                  {m.avgMs > 0 && <Tag label={`${fa(m.avgMs)} میلی‌ثانیه`} />}
                </Box>
                {(m.lastError || m.deadUntil) && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                    {m.lastError}
                    {m.deadUntil ? ` — تا ${faDate(m.deadUntil)} دوباره امتحان نمی‌شود` : ''}
                  </Typography>
                )}
              </div>
            ))}
            {st.models.length > 12 && (
              <Typography variant="caption" color="text.secondary">
                و {fa(st.models.length - 12)} مدل دیگر…
              </Typography>
            )}
          </Stack>
        )}
      </SectionCard>

      {/* پشتیبان‌ها */}
      <SectionCard
        icon={<SwapHorizRoundedIcon />}
        title="سرویس‌های پشتیبان"
        hint="به همین ترتیب امتحان می‌شوند. هرچه بیشتر، احتمال متوقف‌شدن کمتر."
        actions={
          <Button size="small" variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setForm({ ...empty })}>
            افزودن پشتیبان
          </Button>
        }
      >
        {st.fallbacks.length === 0 ? (
          <EmptyHint
            icon={<SwapHorizRoundedIcon />}
            title="هنوز پشتیبانی تعریف نشده"
            hint="اگر سرویس اصلی از کار بیفتد و پشتیبانی نباشد، پیام‌ها فقط در صف می‌مانند."
            action={
              <Button size="small" variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setForm({ ...empty })}>
                افزودن پشتیبان
              </Button>
            }
          />
        ) : (
          <Stack spacing={1}>
            {st.fallbacks.map((f, i) => (
              <div key={f.id} className="ubc-item" style={{ opacity: f.enabled ? 1 : 0.65 }}>
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span className="fac-line-code" style={{ minWidth: 22 }}>{fa(i + 1)}</span>
                  <Typography variant="body2" fontWeight={800}>{f.label}</Typography>
                  {ov?.providerId === f.id && onOverride && <Pill label="الان فعال" color={UB.warn} />}
                  <Box sx={{ flex: 1 }} />
                  <Tooltip title={f.enabled ? 'در زنجیرهٔ جایگزینی هست' : 'کنار گذاشته شده'}>
                    <Switch
                      size="small"
                      checked={f.enabled}
                      disabled={busy}
                      onChange={(e) =>
                        act(() => userbotService.saveFallback({ id: f.id, label: f.label, baseUrl: f.baseUrl, enabled: e.target.checked }))
                      }
                    />
                  </Tooltip>
                  <IconAction
                    title="ویرایش"
                    onClick={() =>
                      setForm({
                        id: f.id,
                        label: f.label,
                        baseUrl: f.baseUrl,
                        apiKey: '',
                        gateModel: f.gateModel,
                        writerModel: f.writerModel,
                        enabled: f.enabled,
                      })
                    }
                  >
                    <SwapHorizRoundedIcon fontSize="small" />
                  </IconAction>
                  <IconAction title="حذف" color="error" disabled={busy} onClick={() => act(() => userbotService.deleteFallback(f.id))}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconAction>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ direction: 'ltr', display: 'block', textAlign: 'right' }}>
                  {f.baseUrl}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                  {f.writerModel && <Tag label={`نویسنده: ${f.writerModel}`} />}
                  {f.gateModel && <Tag label={`دروازه‌بان: ${f.gateModel}`} />}
                  <Tag color={f.apiKeyConfigured ? 'success' : 'warning'} label={f.apiKeyConfigured ? 'کلید دارد' : 'بدون کلید (از اصلی)'} />
                </Box>
              </div>
            ))}
          </Stack>
        )}
      </SectionCard>

      {/* فرم کلید */}
      {keyForm && (
        <ResponsiveModal
          open
          onClose={() => setKeyForm(null)}
          maxWidth="xs"
          disableClose={busy}
          icon={<KeyRoundedIcon />}
          title={keyForm.id ? `ویرایش ${keyForm.label}` : 'کلید API تازه'}
          subtitle="چند کلید روی یک سرویس یعنی وقتی سهمیهٔ یکی تمام شد، بی‌وقفه با بعدی ادامه می‌دهیم."
          actions={
            <>
              <Button size="small" color="inherit" disabled={busy} onClick={() => setKeyForm(null)}>انصراف</Button>
              <Button
                size="small"
                variant="contained"
                disabled={busy || !keyForm.label.trim() || (!keyForm.id && !keyForm.apiKey.trim())}
                onClick={async () => {
                  await act(() => userbotService.saveAiAccount(keyForm));
                  setKeyForm(null);
                }}
              >
                ذخیره
              </Button>
            </>
          }
        >
          <Stack spacing={1.5}>
            <TextField
              select
              size="small"
              label="برای کدام سرویس"
              value={keyForm.providerId}
              onChange={(e) => setKeyForm({ ...keyForm, providerId: e.target.value })}
              fullWidth
              disabled={Boolean(keyForm.id)}
            >
              {st.accounts.map((pv) => (
                <MenuItem key={pv.providerId || 'primary'} value={pv.providerId}>{pv.providerLabel}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="نام"
              value={keyForm.label}
              onChange={(e) => setKeyForm({ ...keyForm, label: e.target.value })}
              fullWidth
              placeholder="مثلاً: جمنای حساب دوم"
            />
            <TextField
              size="small"
              label="کلید API"
              value={keyForm.apiKey}
              onChange={(e) => setKeyForm({ ...keyForm, apiKey: e.target.value })}
              fullWidth
              placeholder={keyForm.id ? 'خالی = بدون تغییر' : ''}
              sx={{ '& input': { direction: 'ltr', textAlign: 'left' } }}
            />
            <Alert severity="info" sx={{ '& .MuiAlert-message': { lineHeight: 1.9 } }}>
              سهمیهٔ رایگان روی حساب بسته می‌شود نه روی مدل، پس کلید دوم باید از یک حساب گوگل/کلودفلر دیگر باشد تا واقعاً سهمیهٔ جدا داشته باشد.
            </Alert>
          </Stack>
        </ResponsiveModal>
      )}

      {/* فرم پشتیبان */}
      {form && (
        <ResponsiveModal
          open
          onClose={() => setForm(null)}
          maxWidth="sm"
          disableClose={busy}
          icon={<SwapHorizRoundedIcon />}
          title={form.id ? `ویرایش ${form.label}` : 'سرویس پشتیبان تازه'}
          subtitle="هر سرویس سازگار با OpenAI. اگر مدل را خالی بگذارید، خودش سبک‌ترین مدل آزاد را پیدا می‌کند."
          actions={
            <>
              <Button size="small" color="inherit" disabled={busy} onClick={() => setForm(null)}>انصراف</Button>
              <Button
                size="small"
                variant="contained"
                disabled={busy || !form.baseUrl.trim() || !form.label.trim()}
                onClick={async () => {
                  await act(() => userbotService.saveFallback(form));
                  setForm(null);
                }}
              >
                ذخیره
              </Button>
            </>
          }
        >
          <Stack spacing={1.5}>
            <TextField
              select
              size="small"
              label="پیش‌تنظیم"
              value=""
              onChange={(e) => {
                const pr = PRESETS.find((x) => x.label === e.target.value);
                if (pr) setForm((f) => (f ? { ...f, label: pr.label, baseUrl: pr.baseUrl, gateModel: pr.gateModel, writerModel: pr.writerModel } : f));
              }}
              fullWidth
              helperText="انتخابش فقط فیلدها را پر می‌کند"
            >
              {PRESETS.map((pr) => (
                <MenuItem key={pr.label} value={pr.label}>{pr.label}</MenuItem>
              ))}
            </TextField>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <TextField size="small" label="نام" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} fullWidth />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  size="small"
                  label="کلید API"
                  value={form.apiKey}
                  onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
                  fullWidth
                  placeholder={form.id ? 'خالی = بدون تغییر' : ''}
                  helperText="خالی یعنی از کلید سرویس اصلی استفاده شود"
                  sx={{ '& input': { direction: 'ltr', textAlign: 'left' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  label="Base URL"
                  value={form.baseUrl}
                  onChange={(e) => setForm({ ...form, baseUrl: e.target.value })}
                  fullWidth
                  sx={{ '& input': { direction: 'ltr', textAlign: 'left' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  size="small"
                  label="مدل دروازه‌بان"
                  value={form.gateModel}
                  onChange={(e) => setForm({ ...form, gateModel: e.target.value })}
                  fullWidth
                  sx={{ '& input': { direction: 'ltr', textAlign: 'left' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  size="small"
                  label="مدل نویسنده"
                  value={form.writerModel}
                  onChange={(e) => setForm({ ...form, writerModel: e.target.value })}
                  fullWidth
                  sx={{ '& input': { direction: 'ltr', textAlign: 'left' } }}
                />
              </Grid>
            </Grid>
            <Alert severity="info" sx={{ '& .MuiAlert-message': { lineHeight: 1.9 } }}>
              ترتیب مهم است: پشتیبان‌ها از بالا به پایین امتحان می‌شوند و اولین سرویسی که واقعاً جواب بدهد پیشنهاد می‌شود.
            </Alert>
          </Stack>
        </ResponsiveModal>
      )}
    </Stack>
  );
};

export default FailoverCard;
